/**
 * FieldDiff Component
 * Displays a single field change with old and new values.
 */

import { cn } from '@/lib/utils';

interface FieldDiffProps {
  fieldName: string;
  oldValue: unknown;
  newValue: unknown;
}

/**
 * Format a value for display.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '(empty)';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    if (value.length === 0) return '(empty)';
    // Truncate long strings
    if (value.length > 100) {
      return `${value.substring(0, 100)}...`;
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    // For arrays (like removed/added images or amenities), just show count
    return `${String(value.length)} item${value.length === 1 ? '' : 's'}`;
  }
  return JSON.stringify(value);
}

/**
 * Get a human-readable label for a field name.
 */
function getFieldLabel(fieldName: string): string {
  const labels: Record<string, string> = {
    name: 'Name',
    description: 'Description',
    addressFull: 'Address',
    addressCity: 'City',
    addressCountry: 'Country',
    latitude: 'Latitude',
    longitude: 'Longitude',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    accommodates: 'Accommodates',
    propertyType: 'Property Type',
    basePrice: 'Base Price',
    currency: 'Currency',
    partnerImages: 'Images',
    amenities: 'Amenities',
  };
  return labels[fieldName] ?? fieldName;
}

export function FieldDiff({ fieldName, oldValue, newValue }: FieldDiffProps): JSX.Element {
  const formattedOld = formatValue(oldValue);
  const formattedNew = formatValue(newValue);

  return (
    <div
      className="flex flex-col gap-1 py-2"
      data-testid={`field-diff-${fieldName}`}
    >
      <span className="text-xs font-medium text-muted-foreground">
        {getFieldLabel(fieldName)}
      </span>
      <div className="flex items-center gap-2 text-sm">
        <span className={cn('line-through text-red-500/80', !formattedOld && 'no-underline')}>
          {formattedOld}
        </span>
        <span className="text-muted-foreground">→</span>
        <span className="text-green-600 font-medium">{formattedNew}</span>
      </div>
    </div>
  );
}
